import { useState } from 'react';
import {
  Box,
  Table,
  TableCell,
  TableRow,
  Button,
  Grid,
  TableBody,
} from '@mui/material';

import { Helmet } from 'react-helmet-async';

import Plan from '../../../../models/Plan/Plan';
import api from '../../../../services/api';
import ModalComponent from '../../../../components/ModalComponent';
import ChangedPlan from './ChangedPlan';

import { useUserContext } from '../../../../contexts/UserContext';

function PlanConfirm({
  plan,
  setSelectedPlan,
  requestPlansList,
}: {
  plan: Plan,
  setSelectedPlan: (plan: Plan) => void,
  requestPlansList: (callback: (isLoading: boolean) => void) => Promise<void>
}) {
  // variables
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [changedPlan, setChangedPlan] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { updatePlanInfo } = useUserContext();

  // functions

  const handleShowModal = () => {
    setOpenModal(!openModal);
  };

  const handleCancel = () => {
    setSelectedPlan(null);
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    await api.post('v1/plans/change-plan', {
      id: plan.default_price,
      lang: navigator.language,
    })
      .then((res) => {
        const { changedSuccessfully, error } = res.data;
        if (error) {
          setErrorMessage(error);
          return;
        }

        if (changedSuccessfully) {
          handleShowModal();
          setChangedPlan(true);
          updatePlanInfo({
            name: plan.name,
            initial: plan.metadata?.plan_initial,
            feat_pages: plan.metadata?.feat_pages,
            price: plan.price,
          });
        }
      }).finally(() => {
        setIsLoading(false);
      });
  };

  // render conditions
  if (changedPlan) {
    return (
      <ChangedPlan
        setSelectedPlan={setSelectedPlan}
        setChangedSuccessfully={setChangedPlan}
        requestPlansList={requestPlansList}
        selectedPlanName={plan.name}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>Change contract plan</title>
      </Helmet>
      <Box className="container-fluid py-5">
        <Box className="text-bold text-center">
          <h2>Change contract plan</h2>
          <p>Your contract plan will be changed. Please review the plan information.</p>
        </Box>
        <Box className="card mt-4">
          <Box className="card-body">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#f5f5f5' }}>Plan name</TableCell>
                  <TableCell>{plan.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#f5f5f5' }}>Start date</TableCell>
                  <TableCell>{plan.expirationDate}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '1rem',
              }}
            >
              <Box className="border p-3">
                <h4 className="text-bold">Important notes about contract plan change</h4>
                <p>
                  - The contract plan change cannot be cancelled.
                  <br />
                  - Once you change your contract plan, you cannot revert to the original plan.
                  <br />
                  - Pages you have created will continue to be available after the change.
                  <br />
                  - Paid plans will start billing from the following month.
                </p>
              </Box>
              <Grid
                gap={2}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '1rem',
                }}
              >
                <Button variant="outlined" onClick={handleCancel}>
                  Back
                </Button>
                <Button variant="contained" onClick={handleShowModal}>
                  Request plan change
                </Button>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>

      <ModalComponent
        title="Change contract plan"
        confirmButtonText="Change contract plan"
        cancelButtonText="Close"
        onConfirm={handleConfirm}
        closeModal={handleShowModal}
        show={openModal}
        loading={isLoading}
        errorMessage={errorMessage}
      >
        Are you sure you want to change your contract plan?
      </ModalComponent>
    </>
  );
}

export default PlanConfirm;
